import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useClientPagination } from '../../lib/usePagination.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { PageHeader } from '../../app/PageHeader.jsx';
import {
  PageActionStrip,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  StatusChip,
  Sparkline,
  MiniBars,
  InspectorCard,
  AlertCard,
  PageViewerControls,
  StatusDonutMini,
  KpiHeroBand,
  deriveKpiTone,
  toast,
  activityRail,
  useInspector,
} from '../../components/index.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import { NewEpicDrawer } from './NewEpicDrawer.jsx';
import { TimeEntryDrawer } from '../time/TimeEntryDrawer.jsx';
import './delivery.css';
import './delivery.redesign.css';

// Executive-traffic-light tone for a free-text risk/dependency severity value.
function severityTone(value) {
  const s = String(value || '').toLowerCase();
  if (/(crit|sev[\s-]?1|blocker|urgent|high)/.test(s)) return 'brick';
  if (/(med|moder|elevat|warn|amber)/.test(s)) return 'amber';
  if (/(low|minor|info|nominal)/.test(s)) return 'sage';
  if (/(open|active|tracked|pending)/.test(s)) return 'blue';
  return 'slate';
}

// Tone for a connector-health status string ("healthy", "warning", "error"…).
function connectorStatusTone(status) {
  const s = String(status || '').toLowerCase();
  if (/healthy|connected|ready|ok|green|succeed/.test(s)) return 'sage';
  if (/warn|degrad/.test(s)) return 'amber';
  if (/error|fail|down|offline/.test(s)) return 'brick';
  return 'outline';
}

// Compact local timestamp for connector-sync / AI-run freshness labels.
// Returns null for absent or unparseable values so callers can omit the claim.
function formatTimestamp(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Tone for the WIP flow-distribution segments (kanban buckets).
const WIP_BUCKET_TONE = {
  todo: 'slate',
  in_progress: 'blue',
  blocked: 'brick',
  review: 'amber',
  done: 'sage',
};

const STATUS_COLUMNS = [
  { key: 'todo', label: 'To do' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'review', label: 'Review' },
  { key: 'done', label: 'Done' },
];
function bucket(status) {
  const s = String(status || '').toLowerCase();
  if (/(todo|backlog|open|new)/.test(s)) return 'todo';
  if (/block/.test(s)) return 'blocked';
  if (/review|qa|test/.test(s)) return 'review';
  if (/done|complete|closed/.test(s)) return 'done';
  return 'in_progress';
}
function typeIcon(t) {
  const x = String(t || '').toLowerCase();
  if (x.includes('bug')) return '🐞';
  if (x.includes('spike')) return '⚡';
  if (x.includes('epic')) return '◆';
  if (x.includes('story')) return '📋';
  return '◇';
}

function upsertEnvelopeRow(prev, record) {
  if (!Array.isArray(prev?.data)) return prev;
  const exists = prev.data.some((item) => item.id === record.id);
  const nextRows = exists
    ? prev.data.map((item) => (item.id === record.id ? { ...item, ...record } : item))
    : [...prev.data, record];
  return {
    ...prev,
    data: nextRows,
    page: prev.page ? { ...prev.page, total: nextRows.length } : prev.page,
  };
}

function removeEnvelopeRow(prev, recordId) {
  if (!Array.isArray(prev?.data)) return prev;
  const nextRows = prev.data.filter((item) => item.id !== recordId);
  return {
    ...prev,
    data: nextRows,
    page: prev.page ? { ...prev.page, total: nextRows.length } : prev.page,
  };
}

export function IntegratedDeliveryWorkspacePage() {
  const [params, setParams] = useSearchParams();
  const projectId = params.get('projectId') || '';
  const projects = useApi('/projects', { query: { limit: 100 } });
  const sprints = useApi('/sprints', { query: { limit: 30 } });
  const workItems = useApi('/work-items', {
    query: projectId ? { projectId, limit: 200 } : { limit: 200 },
  });
  const risks = useApi('/risks', {
    query: projectId
      ? { scopeId: projectId, scopeType: 'project', kind: 'risk' }
      : { kind: 'risk' },
  });
  const dependencies = useApi('/risks', {
    query: projectId
      ? { scopeId: projectId, scopeType: 'project', kind: 'dependency' }
      : { kind: 'dependency' },
  });
  const milestones = useApi('/milestones', {
    query: projectId ? { scopeType: 'project', scopeId: projectId, limit: 50 } : { limit: 50 },
  });
  // TT-06a — connector-status chip binds to the real connector registry. A
  // fetch failure (including 403 for non-admin personas) reads as "not
  // connected"; the UI never asserts an integration claim it cannot back.
  const connectors = useApi('/connectors', { query: { limit: 100 } });
  // TT-06b — the Risk Sentinel card renders only when an actual AI run exists
  // for the portfolio-risk agent. No run record → no card.
  const sentinelRuns = useApi('/ai/runs', {
    query: { agentId: 'AGT-PORTFOLIO-RISK', limit: 10 },
  });
  const update = useMutation();
  const createEpic = useMutation();
  const checklistMutation = useMutation();
  const dependencyMutation = useMutation();
  const inspector = useInspector();
  const [activeSprintId, setActiveSprintId] = useState('');
  const [epicDrawerOpen, setEpicDrawerOpen] = useState(false);
  const [newChecklistLabel, setNewChecklistLabel] = useState('');
  const [newChecklistDueDate, setNewChecklistDueDate] = useState('');
  const [newDependencyPredecessorId, setNewDependencyPredecessorId] = useState('');
  const [newDependencyLagDays, setNewDependencyLagDays] = useState('0');
  const [timeDrawerOpen, setTimeDrawerOpen] = useState(false);
  const { user } = useCurrentUser();
  const selectedWorkItemId = inspector.target?.id || '';
  const checklist = useApi(
    selectedWorkItemId ? `/work-items/${selectedWorkItemId}/checklist` : null,
    {
      skip: !selectedWorkItemId,
      deps: [selectedWorkItemId],
    },
  );
  const dependencyLinks = useApi(
    selectedWorkItemId ? `/work-items/${selectedWorkItemId}/dependencies` : null,
    {
      skip: !selectedWorkItemId,
      deps: [selectedWorkItemId],
    },
  );

  const projectList = projects.data?.data || [];
  const allWorkItems = useMemo(() => workItems.data?.data || [], [workItems.data]);
  const allSprints = (sprints.data?.data || []).filter(
    (s) => !projectId || s.projectId === projectId,
  );
  const activeSprint = allSprints.find((s) => s.id === activeSprintId) || allSprints[0] || null;
  const wis = allWorkItems.filter(
    (w) => !activeSprint || w.sprintId === activeSprint?.id || !w.sprintId,
  );
  const workItemById = useMemo(() => {
    return new Map(allWorkItems.map((item) => [item.id, item]));
  }, [allWorkItems]);
  const checklistItems = checklist.data?.data || [];
  const dependencyRows = useMemo(() => {
    return (dependencyLinks.data?.data || []).map((dependency) => {
      const isPredecessor = dependency.successorId === selectedWorkItemId;
      const relatedId = isPredecessor ? dependency.predecessorId : dependency.successorId;
      const relatedItem = workItemById.get(relatedId);
      return {
        ...dependency,
        relationLabel: isPredecessor ? 'Blocked by' : 'Blocks',
        relatedId,
        relatedTitle: relatedItem?.title || relatedId || 'Unmapped work item',
      };
    });
  }, [dependencyLinks.data, selectedWorkItemId, workItemById]);
  const availablePredecessors = useMemo(() => {
    if (!selectedWorkItemId) return [];
    const existingPredecessorIds = new Set(
      dependencyRows
        .filter((dependency) => dependency.successorId === selectedWorkItemId)
        .map((dependency) => dependency.predecessorId),
    );
    return allWorkItems.filter(
      (item) => item.id !== selectedWorkItemId && !existingPredecessorIds.has(item.id),
    );
  }, [allWorkItems, dependencyRows, selectedWorkItemId]);

  const grouped = useMemo(() => {
    const acc = STATUS_COLUMNS.reduce((a, c) => ((a[c.key] = []), a), {});
    for (const w of wis) acc[bucket(w.status)].push(w);
    return acc;
  }, [wis]);

  useEffect(() => {
    setNewChecklistLabel('');
    setNewChecklistDueDate('');
    setNewDependencyPredecessorId('');
    setNewDependencyLagDays('0');
  }, [selectedWorkItemId]);

  // TT-06a — resolve the real ServiceNow connector record (if any) from the
  // connector registry instead of asserting a static "integration active" chip.
  const serviceNowConnector = useMemo(() => {
    const rows = connectors.data?.data || [];
    return (
      rows.find((c) =>
        /servicenow/i.test(
          [c.sourceSystem, c.systemType, c.system, c.name, c.id].filter(Boolean).join(' '),
        ),
      ) || null
    );
  }, [connectors.data]);
  const serviceNowSyncedAt = formatTimestamp(serviceNowConnector?.lastSyncAt);

  // TT-06b — latest real run for the portfolio-risk agent, newest first.
  const latestSentinelRun = useMemo(() => {
    const rows = sentinelRuns.data?.data || [];
    return (
      rows
        .slice()
        .sort((a, b) =>
          String(b.createdAt || b.startedAt || '').localeCompare(
            String(a.createdAt || a.startedAt || ''),
          ),
        )[0] || null
    );
  }, [sentinelRuns.data]);
  const sentinelRunAt = formatTimestamp(
    latestSentinelRun?.createdAt || latestSentinelRun?.startedAt,
  );

  const velocityWindow = useMemo(() => allSprints.slice(-8), [allSprints]);
  // TT-06c — velocity comes only from sprint records that actually report
  // points. Sprints without any points are omitted; there is no invented
  // fallback value.
  const velocitySeries = useMemo(() => {
    return velocityWindow
      .map((s) => {
        const n = Number(s.velocityPoints ?? s.completedPoints ?? s.committedPoints);
        return Number.isFinite(n) ? { value: n, payload: s.velocityRichTooltip || null } : null;
      })
      .filter(Boolean);
  }, [velocityWindow]);
  const velocityPoints = useMemo(() => velocitySeries.map((p) => p.value), [velocitySeries]);
  const velocityPointPayloads = useMemo(
    () => velocitySeries.map((p) => p.payload),
    [velocitySeries],
  );
  const sprintPager = useClientPagination(allSprints, {
    initialPageSize: 5,
    resetKey: projectId,
  });
  const riskPager = useClientPagination(risks.data?.data || [], {
    initialPageSize: 5,
    resetKey: `risks-${projectId}`,
  });
  const dependencyPager = useClientPagination(dependencies.data?.data || [], {
    initialPageSize: 5,
    resetKey: `dependencies-${projectId}`,
  });
  const velocityCurrent = velocityPoints.length ? velocityPoints[velocityPoints.length - 1] : null;
  const velocityDelta =
    velocityPoints.length > 1 ? velocityCurrent - velocityPoints[velocityPoints.length - 2] : null;

  // WIP flow-distribution segments — derived from the already-grouped kanban
  // buckets. Tone is data-driven (blocked → brick, review → amber, done → sage).
  const wipFlow = useMemo(() => {
    const total = wis.length;
    return STATUS_COLUMNS.map((col) => ({
      key: col.key,
      label: col.label,
      count: grouped[col.key]?.length || 0,
      tone: WIP_BUCKET_TONE[col.key] || 'slate',
    })).map((seg) => ({ ...seg, pct: total ? (seg.count / total) * 100 : 0 }));
  }, [grouped, wis.length]);
  const wipTotal = wis.length;

  // Committed-vs-completed velocity series across the trailing window. Only the
  // bars whose data is present render; absent data omits the comparison block.
  const committedSeries = useMemo(
    () =>
      velocityWindow
        .map((s) => Number(s.committedPoints))
        .map((n) => (Number.isFinite(n) ? n : null)),
    [velocityWindow],
  );
  const completedSeries = useMemo(
    () =>
      velocityWindow
        .map((s) => Number(s.completedPoints ?? s.velocityPoints))
        .map((n) => (Number.isFinite(n) ? n : null)),
    [velocityWindow],
  );
  const hasCommitted = committedSeries.some((n) => n != null);
  const hasCompleted = completedSeries.some((n) => n != null);

  // Risk severity distribution for the StatusDonutMini header rollup.
  const riskRows = useMemo(() => risks.data?.data || [], [risks.data]);
  const riskSeverityDonut = useMemo(() => {
    const counts = { brick: 0, amber: 0, sage: 0, blue: 0, slate: 0 };
    for (const r of riskRows) counts[severityTone(r.severity || r.status)] += 1;
    const labels = {
      brick: 'Critical/High',
      amber: 'Medium',
      sage: 'Low',
      blue: 'Open',
      slate: 'Other',
    };
    return ['brick', 'amber', 'sage', 'blue', 'slate']
      .map((tone) => ({ label: labels[tone], count: counts[tone], tone }))
      .filter((s) => s.count > 0);
  }, [riskRows]);
  const dependencyRowsData = dependencies.data?.data || [];

  // KPI hero band — composed entirely from already-fetched workspace data.
  const blockedCount = grouped.blocked?.length || 0;
  const criticalRiskCount = riskRows.filter(
    (r) => severityTone(r.severity || r.status) === 'brick',
  ).length;
  const kpiItems = useMemo(
    () => [
      {
        id: 'velocity',
        label: 'Sprint velocity',
        value: velocityCurrent ?? '—',
        unit: velocityCurrent != null ? 'pts' : undefined,
        ...(velocityDelta != null
          ? {
              delta: velocityDelta,
              deltaLabel: `${velocityDelta >= 0 ? '+' : ''}${velocityDelta} vs prior`,
            }
          : {}),
        sparkline: velocityPoints.length > 1 ? velocityPoints : undefined,
        sparklineTone: (velocityDelta ?? 0) >= 0 ? 'sage' : 'brick',
        status: velocityCurrent == null ? 'slate' : (velocityDelta ?? 0) >= 0 ? 'sage' : 'amber',
        footer:
          velocityCurrent == null
            ? 'No sprint points reported'
            : `Trailing ${velocityPoints.length} sprint${velocityPoints.length === 1 ? '' : 's'}`,
      },
      {
        id: 'wip',
        label: 'Active WIP',
        value: wipTotal,
        unit: 'items',
        status: 'blue',
        footer: `${grouped.in_progress?.length || 0} in progress · ${grouped.review?.length || 0} in review`,
      },
      {
        id: 'blocked',
        label: 'Blocked work',
        value: blockedCount,
        unit: blockedCount === 1 ? 'item' : 'items',
        status: deriveKpiTone({
          value: blockedCount,
          target: 0,
          invert: true,
          atRisk: blockedCount > 0,
        }),
        statusLabel: blockedCount > 0 ? 'Attention' : 'Clear',
        footer: 'Flow-blocking items',
      },
      {
        id: 'risks',
        label: 'Critical/high risks',
        value: criticalRiskCount,
        unit: criticalRiskCount === 1 ? 'risk' : 'risks',
        status: deriveKpiTone({
          value: criticalRiskCount,
          target: 0,
          invert: true,
          atRisk: criticalRiskCount > 0,
        }),
        statusLabel: criticalRiskCount > 0 ? 'Watch' : 'Stable',
        footer: `${riskRows.length} total open · ${dependencyRowsData.length} dependencies`,
      },
    ],
    [
      velocityCurrent,
      velocityDelta,
      velocityPoints,
      wipTotal,
      grouped,
      blockedCount,
      criticalRiskCount,
      riskRows.length,
      dependencyRowsData.length,
    ],
  );

  async function moveItem(item, targetBucket) {
    if (!item) return;
    const statusMap = {
      todo: 'open',
      in_progress: 'in_progress',
      blocked: 'blocked',
      review: 'in_review',
      done: 'done',
    };
    try {
      const res = await update.run(() =>
        api.patch(`/work-items/${item.id}`, { status: statusMap[targetBucket] }),
      );
      const updated = res.data || res;
      workItems.setData?.((prev) => {
        if (!Array.isArray(prev?.data)) return prev;
        return {
          ...prev,
          data: prev.data.map((w) => (w.id === item.id ? { ...w, ...updated } : w)),
        };
      });
      toast.success({
        title: 'Work item updated',
        body: `${item.title} → ${targetBucket.replace('_', ' ')}`,
      });
      activityRail.push({
        action: 'work_item.update',
        targetType: 'work_item',
        targetId: item.id,
        actorUserId: user?.id || 'unknown',
        summary: `${item.title} → ${targetBucket}`,
      });
    } catch (e) {
      toast.error({ title: 'Could not update', body: e.message });
    }
  }

  async function handleCreateEpic(payload) {
    try {
      const res = await createEpic.run(() => api.post('/work-items', payload));
      const created = res.data || res;
      // Prepend the new epic into local kanban data.
      workItems.setData?.((prev) => {
        const current = Array.isArray(prev?.data) ? prev.data : [];
        return prev
          ? {
              ...prev,
              data: [created, ...current],
              page: prev.page ? { ...prev.page, total: current.length + 1 } : prev.page,
            }
          : prev;
      });
      setEpicDrawerOpen(false);
      toast.success({
        title: 'Epic created',
        body: `${created.id} · ${created.title}`,
        action: {
          label: 'View on roadmap →',
          onClick: () => window.location.assign(`/roadmap?focus=${created.id}`),
        },
      });
      activityRail.push({
        action: 'work_item.create',
        targetType: 'work_item',
        targetId: created.id,
        actorUserId: user?.id || 'unknown',
        summary: `Created epic ${created.title?.slice(0, 64)} on ${created.projectId}`,
      });
    } catch (e) {
      toast.error({ title: 'Could not create epic', body: e.message });
    }
  }

  async function addChecklistItem() {
    if (!selectedWorkItemId || !newChecklistLabel.trim()) return;
    try {
      const payload = {
        label: newChecklistLabel.trim(),
        dueDate: newChecklistDueDate || null,
      };
      const response = await checklistMutation.run(() =>
        api.post(`/work-items/${selectedWorkItemId}/checklist`, payload),
      );
      const created = response.data || response;
      checklist.setData?.((prev) => upsertEnvelopeRow(prev, created));
      setNewChecklistLabel('');
      setNewChecklistDueDate('');
      toast.success({
        title: 'Checklist item created',
        body: created.label,
      });
      activityRail.push({
        action: 'work_item.checklist.create',
        targetType: 'work_item',
        targetId: selectedWorkItemId,
        actorUserId: user?.id || 'unknown',
        summary: `Added checklist item ${created.label?.slice(0, 64)}`,
      });
    } catch (error) {
      toast.error({ title: 'Could not add checklist item', body: error.message });
    }
  }

  async function updateChecklistItem(item, status) {
    if (!selectedWorkItemId || !item) return;
    try {
      const response = await checklistMutation.run(() =>
        api.patch(`/work-items/${selectedWorkItemId}/checklist/${item.id}`, {
          ...item,
          status,
        }),
      );
      const updated = response.data || response;
      checklist.setData?.((prev) => upsertEnvelopeRow(prev, updated));
      toast.success({
        title: 'Checklist updated',
        body: `${item.label} → ${status.replace('_', ' ')}`,
      });
      activityRail.push({
        action: 'work_item.checklist.update',
        targetType: 'work_item',
        targetId: selectedWorkItemId,
        actorUserId: user?.id || 'unknown',
        summary: `Checklist item ${item.id} updated to ${status}`,
      });
    } catch (error) {
      toast.error({ title: 'Could not update checklist item', body: error.message });
    }
  }

  async function addDependencyLink() {
    if (!selectedWorkItemId || !newDependencyPredecessorId) return;
    try {
      const payload = {
        predecessorId: newDependencyPredecessorId,
        dependencyType: 'finish_to_start',
        lagDays: Number(newDependencyLagDays || 0),
      };
      const response = await dependencyMutation.run(() =>
        api.post(`/work-items/${selectedWorkItemId}/dependencies`, payload),
      );
      const created = response.data || response;
      dependencyLinks.setData?.((prev) => upsertEnvelopeRow(prev, created));
      setNewDependencyPredecessorId('');
      setNewDependencyLagDays('0');
      toast.success({
        title: 'Dependency linked',
        body: `${payload.predecessorId} now gates ${selectedWorkItemId}`,
      });
      activityRail.push({
        action: 'work_item.dependency.create',
        targetType: 'work_item',
        targetId: selectedWorkItemId,
        actorUserId: user?.id || 'unknown',
        summary: `Linked predecessor ${payload.predecessorId} to ${selectedWorkItemId}`,
      });
    } catch (error) {
      toast.error({ title: 'Could not link dependency', body: error.message });
    }
  }

  async function removeDependencyLink(dependency) {
    if (!selectedWorkItemId || !dependency?.id) return;
    try {
      await dependencyMutation.run(() =>
        api.delete(`/work-items/${selectedWorkItemId}/dependencies/${dependency.id}`),
      );
      dependencyLinks.setData?.((prev) => removeEnvelopeRow(prev, dependency.id));
      toast.success({
        title: 'Dependency removed',
        body: `${dependency.id} is no longer linked`,
      });
      activityRail.push({
        action: 'work_item.dependency.delete',
        targetType: 'work_item',
        targetId: selectedWorkItemId,
        actorUserId: user?.id || 'unknown',
        summary: `Removed dependency ${dependency.id} from ${selectedWorkItemId}`,
      });
    } catch (error) {
      toast.error({ title: 'Could not remove dependency', body: error.message });
    }
  }

  const projectName = projectList.find((p) => p.id === projectId)?.name;
  usePageMetadata({
    title: projectName ? `${projectName} delivery cockpit` : 'Delivery Workspace',
    subtitle: 'Sprint health, kanban flow, milestones, dependencies, and connector sync.',
  });

  if (projects.loading) return <LoadingSkeleton rows={6} />;
  if (projects.error) return <ErrorState error={projects.error} />;

  return (
    <>
      {/* PRIME-SHELL-UX-001: single body page identity + relocated About. */}
      <PageHeader />
      <PageActionStrip
        filters={
          <select
            className="exec-filter"
            value={projectId}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set('projectId', e.target.value);
              else next.delete('projectId');
              setParams(next, { replace: true });
            }}
          >
            <option value="">All projects</option>
            {projectList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        }
        actions={
          <>
            {/* TT-06a / AC-TRUST-6 — the chip reflects the real connector
                record (status + last sync) or an honest "Not connected". */}
            {connectors.loading ? null : serviceNowConnector ? (
              <span
                className={`chip ${connectorStatusTone(serviceNowConnector.status)} sm`}
                data-testid="delivery-connector-chip"
                title={`Connector ${serviceNowConnector.id}`}
              >
                <span className="chip-dot" />
                {serviceNowConnector.name || 'ServiceNow'} ·{' '}
                {serviceNowConnector.status || 'status unknown'}
                {serviceNowSyncedAt ? ` · synced ${serviceNowSyncedAt}` : ''}
              </span>
            ) : (
              <span className="chip outline sm" data-testid="delivery-connector-chip">
                ServiceNow · Not connected
              </span>
            )}
            <button className="btn primary tactile-press" onClick={() => setEpicDrawerOpen(true)}>
              New epic
            </button>
          </>
        }
      />

      <KpiHeroBand
        eyebrow={projectName ? `${projectName} delivery posture` : 'Delivery posture'}
        asOf={workItems.loadedAt}
        items={kpiItems}
        columns={4}
      />

      <section className="bento delivery-bento delivery-redesign">
        <div className="span-9 card flush delivery-board">
          <header
            className="row spread"
            style={{ padding: 'var(--space-4) var(--space-5)', borderBottom: 'var(--hairline)' }}
          >
            <div>
              <div className="eyebrow">Active sprint</div>
              <h2 className="card-title">{activeSprint?.name || 'Sprint not selected'}</h2>
              <p className="muted small">
                {activeSprint?.startDate || '—'} → {activeSprint?.endDate || '—'} ·{' '}
                {activeSprint?.committedPoints || activeSprint?.velocityPoints || '—'} points
                committed
              </p>
            </div>
            <div className="row" style={{ gap: 8 }}>
              {sprintPager.items.map((s) => (
                <button
                  key={s.id}
                  className={`chip outline sm ${s.id === activeSprint?.id ? 'is-selected' : ''}`}
                  onClick={() => setActiveSprintId(s.id)}
                >
                  {s.id}
                </button>
              ))}
            </div>
          </header>
          <div style={{ padding: '0 var(--space-5) var(--space-3)' }}>
            <PageViewerControls
              page={sprintPager.page}
              label="sprints"
              pageSize={sprintPager.pageSize}
              onOffsetChange={sprintPager.setOffset}
              onPageSizeChange={sprintPager.setPageSize}
            />
          </div>
          {wipTotal > 0 ? (
            <div className="wip-flow" data-testid="delivery-wip-flow">
              <div className="wip-flow__head">
                <span className="eyebrow">Work-in-progress flow</span>
                <span className="muted small">{wipTotal} items in active sprint</span>
              </div>
              <div
                className="wip-flow__bar"
                role="img"
                aria-label={`WIP distribution: ${wipFlow
                  .filter((s) => s.count > 0)
                  .map((s) => `${s.label} ${s.count}`)
                  .join(', ')}`}
              >
                {wipFlow
                  .filter((s) => s.count > 0)
                  .map((s) => (
                    <span
                      key={s.key}
                      className={`wip-flow__seg tone-${s.tone}`}
                      style={{ flex: `${s.count} 0 0` }}
                      title={`${s.label}: ${s.count}`}
                    />
                  ))}
              </div>
              <ul className="wip-flow__legend">
                {wipFlow
                  .filter((s) => s.count > 0)
                  .map((s) => (
                    <li key={s.key}>
                      <span className={`wip-flow__dot tone-${s.tone}`} aria-hidden />
                      {s.label}
                      <span className="wip-flow__count">{s.count}</span>
                    </li>
                  ))}
              </ul>
            </div>
          ) : null}
          <div className="delivery-kanban-shell">
            <div className="delivery-kanban">
              {STATUS_COLUMNS.map((c) => (
                <DeliveryKanbanColumn
                  key={c.key}
                  column={c}
                  items={grouped[c.key]}
                  onOpen={(it) => inspector.open(it)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="span-3 stack">
          <article className="card sprint-velocity" data-testid="delivery-velocity-card">
            <header>
              <div className="eyebrow">Sprint velocity</div>
              <div className="velocity-value">
                <span className="mono">{velocityCurrent ?? '—'}</span>
                {velocityDelta != null ? (
                  <span className={`chip ${velocityDelta >= 0 ? 'sage' : 'red'} sm`}>
                    {velocityDelta >= 0 ? '+' : ''}
                    {velocityDelta}
                  </span>
                ) : null}
              </div>
              {velocityPoints.length > 1 ? (
                <Sparkline
                  values={velocityPoints}
                  pointPayloads={velocityPointPayloads}
                  width={220}
                  height={36}
                  tone="sage"
                  surface="delivery"
                  seriesId="velocity"
                  entityLabel="Sprint velocity"
                />
              ) : null}
              <p className="muted small">
                {velocityPoints.length
                  ? `Trailing ${velocityPoints.length} sprint${velocityPoints.length === 1 ? '' : 's'} · points completed`
                  : 'No sprint points reported yet.'}
              </p>
            </header>
            {hasCommitted || hasCompleted ? (
              <div className="velocity-compare" data-testid="delivery-velocity-compare">
                {hasCommitted ? (
                  <div className="velocity-compare__row">
                    <span className="velocity-compare__label">Committed</span>
                    <MiniBars
                      values={committedSeries.map((n) => n ?? 0)}
                      width={160}
                      height={28}
                      tone="slate"
                      ariaLabel="Committed points per sprint"
                    />
                  </div>
                ) : null}
                {hasCompleted ? (
                  <div className="velocity-compare__row">
                    <span className="velocity-compare__label">Completed</span>
                    <MiniBars
                      values={completedSeries.map((n) => n ?? 0)}
                      width={160}
                      height={28}
                      tone="sage"
                      ariaLabel="Completed points per sprint"
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </article>
          <article className="card key-milestones">
            <header>
              <div className="eyebrow">Key milestones</div>
              <h3 className="card-title">Next IOC</h3>
            </header>
            <ul>
              {(() => {
                const all = milestones.data?.data || [];
                const sorted = [...all].sort((a, b) => {
                  const da = a.targetDate || a.dueDate || '9999-12-31';
                  const db = b.targetDate || b.dueDate || '9999-12-31';
                  return String(da).localeCompare(String(db));
                });
                const list = projectId
                  ? sorted.slice(0, 5)
                  : (() => {
                      const seen = new Set();
                      const out = [];
                      for (const m of sorted) {
                        const key = (m.name || m.title || '') + '|' + (m.projectId || '');
                        if (seen.has(key)) continue;
                        seen.add(key);
                        out.push(m);
                        if (out.length >= 5) break;
                      }
                      return out;
                    })();
                if (list.length === 0) {
                  return <li className="muted small">No milestones for this project.</li>;
                }
                const projectNameById = Object.fromEntries(
                  projectList.map((p) => [p.id, p.name || p.id]),
                );
                return list.map((m, i) => {
                  const isComplete =
                    String(m.status || '')
                      .toLowerCase()
                      .includes('done') || String(m.status || '').includes('complete');
                  const projLabel = !projectId && m.projectId ? projectNameById[m.projectId] : null;
                  return (
                    <li
                      key={m.id}
                      className={
                        isComplete
                          ? 'milestone-complete'
                          : i === 0
                            ? 'milestone-active'
                            : 'milestone-pending'
                      }
                    >
                      <span className="milestone-ring" aria-hidden>
                        {isComplete ? '✓' : i === 0 ? '●' : '○'}
                      </span>
                      <div className="stack tight">
                        <strong>{m.name || m.title}</strong>
                        <span className="muted small">
                          {m.targetDate || m.dueDate || '—'}
                          {projLabel ? ` · ${projLabel}` : ''}
                        </span>
                      </div>
                    </li>
                  );
                });
              })()}
            </ul>
          </article>
          {/* TT-06b — rendered only from a real AI run record; omitted when the
              portfolio-risk agent has no runs (or the runs feed is unavailable). */}
          {latestSentinelRun ? (
            <AlertCard
              severity="blue"
              title="Risk Sentinel"
              body={latestSentinelRun.summary || `Run ${latestSentinelRun.id} recorded.`}
              meta={sentinelRunAt ? `Last run ${sentinelRunAt}` : undefined}
              chips={[
                latestSentinelRun.agentId,
                latestSentinelRun.kind ? String(latestSentinelRun.kind).replace(/_/g, ' ') : null,
              ].filter(Boolean)}
              actionLabel="Open AI orchestration"
              onAction={() => (window.location.href = '/ai/orchestration')}
              testId="delivery-risk-sentinel-card"
            />
          ) : null}
        </div>

        <div className="span-6 card">
          <header className="risk-card-head">
            <div>
              <div className="eyebrow">Active risks</div>
              <h2 className="card-title">{riskRows.length} open</h2>
            </div>
            {riskSeverityDonut.length ? (
              <StatusDonutMini
                segments={riskSeverityDonut}
                size={72}
                totalLabel="risks"
                ariaLabel="Risk severity distribution"
              />
            ) : null}
          </header>
          {riskRows.length === 0 ? (
            <EmptyState title="No active risks" />
          ) : (
            <ul className="risk-list">
              {riskPager.items.map((r) => (
                <li key={r.id} className={`sev-${severityTone(r.severity || r.status)}`}>
                  <strong>{r.title || r.name}</strong>
                  <div className="row spread">
                    <span className="muted small">
                      {r.id} · <PersonLabel userId={r.ownerUserId} fallback="Unowned" />
                    </span>
                    <StatusChip status={r.severity || r.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <PageViewerControls
            page={riskPager.page}
            label="risks"
            pageSize={riskPager.pageSize}
            onOffsetChange={riskPager.setOffset}
            onPageSizeChange={riskPager.setPageSize}
          />
        </div>
        <div className="span-6 card">
          <header className="row spread">
            <div>
              <div className="eyebrow">Dependencies &amp; blockers</div>
              <h2 className="card-title">{dependencyRowsData.length} tracked</h2>
            </div>
          </header>
          {dependencyRowsData.length === 0 ? (
            <EmptyState title="No external dependencies" />
          ) : (
            <ul className="risk-list">
              {dependencyPager.items.map((d) => (
                <li key={d.id} className={`sev-${severityTone(d.severity || d.status)}`}>
                  <strong>{d.title || d.name}</strong>
                  <div className="row spread">
                    <span className="muted small">
                      {String(d.dependencyType || 'external')
                        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                        .replace(/[_-]+/g, ' ')
                        .toLowerCase()}{' '}
                      · {d.externalSystem || d.id}
                    </span>
                    <StatusChip status={d.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <PageViewerControls
            page={dependencyPager.page}
            label="dependencies"
            pageSize={dependencyPager.pageSize}
            onOffsetChange={dependencyPager.setOffset}
            onPageSizeChange={dependencyPager.setPageSize}
          />
        </div>
      </section>

      <InspectorCard
        open={inspector.isOpen}
        onClose={inspector.close}
        eyebrow="Work item"
        title={inspector.target?.title || ''}
        actions={
          <>
            {inspector.target ? (
              <Link
                className="btn secondary sm"
                to={`/work-items/${inspector.target.id}?returnTo=${encodeURIComponent(
                  `/delivery${projectId ? `?projectId=${projectId}` : ''}`,
                )}`}
              >
                Open detail
              </Link>
            ) : null}
            <button className="btn secondary sm" onClick={() => setTimeDrawerOpen(true)}>
              Log time
            </button>
            <button
              className="btn ghost sm"
              onClick={() => moveItem(inspector.target, 'in_progress')}
              disabled={update.running}
            >
              Move → in progress
            </button>
            <button
              className="btn ghost sm"
              onClick={() => moveItem(inspector.target, 'review')}
              disabled={update.running}
            >
              → review
            </button>
            <button
              className="btn success sm"
              onClick={() => moveItem(inspector.target, 'done')}
              disabled={update.running}
            >
              Done
            </button>
          </>
        }
      >
        {inspector.target ? (
          <>
            <div className="row" style={{ gap: 8 }}>
              <span className="chip outline sm">{inspector.target.id}</span>
              <StatusChip status={inspector.target.status} />
              <span className="chip outline sm">{inspector.target.type || 'story'}</span>
            </div>
            <p>
              {inspector.target.description?.slice(0, 400) ||
                inspector.target.summary?.slice(0, 400) ||
                '—'}
            </p>
            <dl className="inspector-dl">
              <div>
                <dt>Assigned</dt>
                <dd>
                  <PersonLabel userId={inspector.target.assignedToUserId} fallback="—" />
                </dd>
              </div>
              <div>
                <dt>Sprint</dt>
                <dd>{inspector.target.sprintId || '—'}</dd>
              </div>
              <div>
                <dt>Priority</dt>
                <dd>{inspector.target.priority || '—'}</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd className="mono">
                  {inspector.target.sourceSystem || inspector.target.dataTrust?.sourceSystem || '—'}
                </dd>
              </div>
            </dl>
            <section className="delivery-inspector-section">
              <div className="row spread">
                <div>
                  <h4 className="delivery-inspector-title">Execution checklist</h4>
                  <p className="muted small">Track entry/exit steps for this work item.</p>
                </div>
                <span className="chip outline sm">{checklistItems.length} items</span>
              </div>
              {checklist.loading ? (
                <div className="muted small">Loading checklist…</div>
              ) : checklistItems.length === 0 ? (
                <div className="kanban-empty">No checklist items yet.</div>
              ) : (
                <ul className="delivery-checklist-list">
                  {checklistItems.map((item) => (
                    <li key={item.id} className="delivery-checklist-row">
                      <div className="stack tight">
                        <strong>{item.label}</strong>
                        <span className="muted small">
                          <PersonLabel userId={item.ownerUserId} fallback="Unassigned" />
                          {item.dueDate ? ` · due ${item.dueDate}` : ''}
                        </span>
                      </div>
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <StatusChip status={item.status || 'open'} />
                        <button
                          className="btn ghost sm"
                          onClick={() =>
                            updateChecklistItem(item, item.status === 'done' ? 'open' : 'done')
                          }
                          disabled={checklistMutation.running}
                        >
                          {item.status === 'done' ? 'Reopen' : 'Mark done'}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="delivery-inline-form">
                <label className="delivery-inline-field">
                  <span>Checklist item label</span>
                  <input
                    value={newChecklistLabel}
                    onChange={(event) => setNewChecklistLabel(event.target.value)}
                    placeholder="Add a delivery validation step"
                  />
                </label>
                <label className="delivery-inline-field">
                  <span>Due date</span>
                  <input
                    type="date"
                    value={newChecklistDueDate}
                    onChange={(event) => setNewChecklistDueDate(event.target.value)}
                  />
                </label>
                <button
                  className="btn secondary sm"
                  onClick={addChecklistItem}
                  disabled={checklistMutation.running || !newChecklistLabel.trim()}
                >
                  Add checklist item
                </button>
              </div>
            </section>
            <section className="delivery-inspector-section">
              <div className="row spread">
                <div>
                  <h4 className="delivery-inspector-title">Dependency links</h4>
                  <p className="muted small">
                    Manage predecessor ties that shape the critical path.
                  </p>
                </div>
                <span className="chip outline sm">{dependencyRows.length} links</span>
              </div>
              {dependencyLinks.loading ? (
                <div className="muted small">Loading dependencies…</div>
              ) : dependencyRows.length === 0 ? (
                <div className="kanban-empty">No linked dependencies yet.</div>
              ) : (
                <ul className="delivery-dependency-list">
                  {dependencyRows.map((dependency) => (
                    <li key={dependency.id} className="delivery-dependency-row">
                      <div className="stack tight">
                        <strong>
                          {dependency.relationLabel}: {dependency.relatedId}
                        </strong>
                        <span>{dependency.relatedTitle}</span>
                        <span className="muted small">
                          {dependency.dependencyType?.replace(/_/g, ' ') || 'finish to start'}
                          {typeof dependency.lagDays === 'number'
                            ? ` · ${dependency.lagDays} day lag`
                            : ''}
                        </span>
                      </div>
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                        {dependency.criticalPathFlag ? (
                          <span className="chip red sm">Critical path</span>
                        ) : null}
                        <button
                          className="btn ghost sm"
                          onClick={() => removeDependencyLink(dependency)}
                          disabled={dependencyMutation.running}
                          aria-label={`Remove dependency ${dependency.id}`}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="delivery-inline-form">
                <label className="delivery-inline-field">
                  <span>Predecessor work item</span>
                  <select
                    value={newDependencyPredecessorId}
                    onChange={(event) => setNewDependencyPredecessorId(event.target.value)}
                  >
                    <option value="">Select predecessor</option>
                    {availablePredecessors.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.id} · {item.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="delivery-inline-field">
                  <span>Lag days</span>
                  <input
                    type="number"
                    min="0"
                    value={newDependencyLagDays}
                    onChange={(event) => setNewDependencyLagDays(event.target.value)}
                  />
                </label>
                <button
                  className="btn secondary sm"
                  onClick={addDependencyLink}
                  disabled={dependencyMutation.running || !newDependencyPredecessorId}
                >
                  Link predecessor
                </button>
              </div>
            </section>
          </>
        ) : null}
      </InspectorCard>

      <NewEpicDrawer
        open={epicDrawerOpen}
        onClose={() => setEpicDrawerOpen(false)}
        onSubmit={handleCreateEpic}
        submitting={createEpic.running}
        defaults={{ projectId, ownerUserId: user?.id }}
      />

      <TimeEntryDrawer
        open={timeDrawerOpen}
        onClose={() => setTimeDrawerOpen(false)}
        targetEntityType="work_item"
        targetEntityId={inspector.target?.id || ''}
        targetTitle={inspector.target?.title || inspector.target?.id || ''}
        projectId={inspector.target?.projectId || projectId || null}
        workItemId={inspector.target?.id || null}
        category="delivery"
      />
    </>
  );
}

function DeliveryKanbanColumn({ column, items, onOpen }) {
  const pager = useClientPagination(items, {
    initialPageSize: 8,
    resetKey: column.key,
  });
  return (
    <div className="kanban-col">
      <header className="row spread">
        <strong>{column.label}</strong>
        <span className="chip outline sm">{items.length}</span>
      </header>
      <div className="kanban-col-body">
        {items.length === 0 ? (
          <div className="kanban-empty">No items</div>
        ) : (
          pager.items.map((it) => (
            <button
              key={it.id}
              className={`story-card ${bucket(it.status)}`}
              onClick={() => onOpen?.(it)}
            >
              <header>
                <span className="story-icon" aria-hidden>
                  {typeIcon(it.type)}
                </span>
                <span className="story-id mono">{it.id?.replace(/^WI-/, 'DHA-') || it.id}</span>
              </header>
              <div className="story-title">{it.title}</div>
              <div className="row spread story-meta">
                <StatusChip status={it.status} />
                <span className="story-avatar">
                  {(it.assignedToUserId || '??').slice(-2).toUpperCase()}
                </span>
              </div>
            </button>
          ))
        )}
      </div>
      <PageViewerControls
        page={pager.page}
        label={`${column.label.toLowerCase()} work items`}
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />
    </div>
  );
}
